import intl from "react-intl-universal";
import classNames from "classnames";
import React, {useCallback, useState} from "react";
import {useSwipeable} from 'react-swipeable';
import {useAuthService, useDataClientService, useRootStore, useUserState} from "@stores/AppStateContext";
import {IDataClientService} from "../../../../../platform/services/data/DataClientService";
import {IAuthService} from "../../../../../platform/services/auth/AuthService";

import type {UserSessionState} from "@stores/UserSessionState";
import type {RootStoreModel} from "@stores/RootStore";
import {Notification} from "../../../../../API";

import {debounce} from "../../../../../utils/debounce";
import {Tooltip} from "../../../../components/Tooltip/Tooltip"
import {ArticleIcon} from "../../../../components/icons/ArticleIcon";
import {WorkspaceIcon} from "../../../../components/icons/WorkspaceIcon";
import {MentionIcon} from "../../../../components/icons/editor/MentionIcon";
import {QuizIcon} from "../../../../components/icons/QuizIcon";
import {PersonAdd} from "../../../../components/icons/PersonAdd";
import {UserAvatar} from "../../../../components/Avatar/Avatar";
import {NotificationAvatarBadge} from "./NotificationAvatarBadge";
import {DeleteIcon} from "../../../../components/icons/DeleteIcon";
import {NotificationCardBadge} from "./NotificationCardBadge";
import {formatDateShort} from "../../../../../utils/date";
import {MarkEmailUnread} from "../../../../components/icons/MarkEmailUnread";
import {MarkEmailRead} from "../../../../components/icons/MarkEmailRead";
import {hideNotification, setNotificationStatus} from "../../../../../actions/notification/setNotificationStatus";

import {PAGES} from "@stores/ViewStore";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import "./NotficationCard.css";


interface ItemRelatedNotificationMeta {
    wsId: string;
    itemId: string;
    itemName: string;
}

const getNotificationMsg = (rootStore: RootStoreModel, userState: UserSessionState, ntf: Notification) => {
    const initiatorUser = userState?.usersDS.users.get(ntf.initiatorId);
    let msgReactElement;
    let onClick = () => {
    };
    switch (ntf.type) {
        case 'ack_cancel':
        case 'ack_require': {
            const ntfMeta = JSON.parse(ntf.meta) as ItemRelatedNotificationMeta;
            const relatedWs = userState!.getWsById(ntfMeta.wsId);
            const itemName = ntfMeta.itemName || intl.get('common.untitled_item').d('Untitled page');
            const params = {
                triggeredUser: initiatorUser.name,
                itemName: itemName,
                itemHref: rootStore.view.getFullUrl(PAGES.DOC_EDIT, {
                    id: ntfMeta.itemId,
                    itemName: itemName,
                    wsSlug: relatedWs?.slug || '',
                }),
            };
            onClick = () => {
                rootStore.view.openPageByName(PAGES.DOC_EDIT, {
                    id: ntfMeta.itemId,
                    wsSlug: relatedWs?.slug || '',
                });
            }
            if (ntf.type === 'ack_require') {
                msgReactElement = intl.getHTML('notification.requiresAcknowledge', params)
                    .d(`<b>{triggeredUser}</b> requires you to acknowledge the Page <a href="{itemHref}">{itemName}</a>`)
            } else {
                msgReactElement = intl.getHTML('notification.cancelAcknowledge', params)
                    .d(`<b>{triggeredUser}</b> has canceled the acknowledgment requirement for the Page  <a href="{itemHref}">{itemName}</a>`)
            }
            break;
        }
        // case 'archivedArticle': {
        //     return intl.getHTML('notification.archivedArticle', {
        //         triggeredUser: initiatorUser.name,
        //         relatedItem: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}</b> archived the Page you created, {relatedItem}. You can still access it in the archives if needed.')
        // }
        // case 'deleteArticle': {
        //     return intl.getHTML('notification.deleteArticle', {
        //         triggeredUser: initiatorUser.name,
        //         relatedItem: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}</b> has moved the Page you created, {relatedItem} to the Trash. You can restore it, or it will be deleted when Trash is emptied.')
        // }
        // case 'addInWorkspace': {
        //     return intl.getHTML('notification.addInWorkspace', {
        //         triggeredUser: initiatorUser.name,
        //         workspace: data.workspace?.name,
        //     }).d('<b>{triggeredUser}</b> added you to Workspace: <b>{workspace}</b>. Welcome aboard!')
        // }
        // case 'removeWorkspace': {
        //     return intl.getHTML('notification.removeWorkspace', {
        //         triggeredUser: initiatorUser.name,
        //         workspace: data.workspace?.name,
        //     }).d('<b>{triggeredUser}</b> has removed you from Workspace: <b>{workspace}</b>.')
        // }
        // case 'articleMention': {
        //     return intl.getHTML('notification.articleMention', {
        //         triggeredUser: initiatorUser.name,
        //         relatedItem: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}</b> mentioned you in Page on {relatedItem}.')
        // }
        // case 'takeQuiz': {
        //     return intl.getHTML('notification.takeQuiz', {
        //         triggeredUser: initiatorUser.name,
        //         relatedItem: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}.</b> requires you to take the test {relatedItem}.')
        // }
        // case 'retakeQuiz': {
        //     return intl.getHTML('notification.retakeQuiz', {
        //         triggeredUser: initiatorUser.name,
        //         relatedItem: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}.</b> requires you to retake the test {relatedItem}.')
        // }
        // case 'updatePermission': {
        //     return intl.getHTML('notification.updatePermission', {
        //         triggeredUser: initiatorUser.name,
        //         role: data.relatedItem?.name,
        //     }).d('<b>{triggeredUser}</b> has updated your role to {role}.')
        // }
        // case 'newUser': {
        //     return intl.getHTML('notification.newUser', {
        //         triggeredUser: initiatorUser.name,
        //     }).d('<b>{triggeredUser}</b> has joined the company.')
        // }
        default:
            msgReactElement = ntf.type;
    }

    return (
        <div onClick={(event) => {
            /* @ts-ignore */
            if (event.target.tagName !== 'A') {
                return;
            }
            event.preventDefault();

            onClick();
        }}>
            {msgReactElement}
        </div>
    )
}

const updateServerReadStatusDebounced = debounce((
    dataClientService: IDataClientService,
    authService: IAuthService,
    notification,
    isRead
) => {
    setNotificationStatus(dataClientService, authService, notification, isRead ? 'unread' : 'read');
}, 250)

interface NotificationCardProps {
    notification: Notification;
}

export const NotificationCard = (
    {
        notification,
    }: NotificationCardProps
) => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const dataClientService = useDataClientService();
    const authService = useAuthService();
    const [swipedRight, setSwipedRight] = useState(false);
    const [swipedLeft, setSwipedLeft] = useState(false);
    const [isRead, setIsRead] = useState(notification.status === 'read');
    const isMobile = screenSizeProvider.view !== 'desktop';

    const onToggleRead = useCallback(
        () => {
            setIsRead(!isRead);
            updateServerReadStatusDebounced(
                dataClientService,
                authService,
                notification, isRead);
        },
        [isRead]
    );

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            if (!swipedLeft && !swipedRight) {
                setSwipedLeft(true);
            }
            if (swipedRight) {
                setSwipedRight(false)
            }
        },
        onSwipedRight: () => {
            if (!swipedRight && !swipedLeft) {
                setSwipedRight(true);
            }
            if (swipedLeft) {
                setSwipedLeft(false);
            }
        },
        trackMouse: false,
    });

    const initiatorUser = notification.initiatorId && userState?.usersDS.users.get(notification.initiatorId);
    const ntfMeta = JSON.parse(notification.meta) as ItemRelatedNotificationMeta;
    const relatedWs = userState!.getWsById(ntfMeta.wsId);

    return (
        <div className="NotificationCard_background">
            <DeleteIcon className={"NotificationCard_background_deleteIcon"} type={"danger"} size={24}
                        onClick={() => hideNotification(dataClientService, authService, notification)}/>
            {isRead
                ? <MarkEmailUnread className={"NotificationCard_background_markIcon"} size={24} onClick={onToggleRead}/>
                : <MarkEmailRead className={"NotificationCard_background_markIcon"} size={24} onClick={onToggleRead}/>
            }
            <div
                {...handlers}
                className={classNames({
                    "NotificationCard": true,
                    "NotificationCard--isRead": isRead,
                    "NotificationCard--swiped--left": swipedLeft,
                    "NotificationCard--swiped--right": swipedRight,
                })}>
                <div className="NotificationCard__AvatarPath">
                    {
                        initiatorUser
                            ? (
                                <NotificationAvatarBadge
                                    isRead={isRead}
                                    badgeContent={
                                        (() => {
                                            const Icon = getNotificationIcon(notification.type);
                                            return Icon ?
                                                <Icon size={14} className="NotificationCard__IconColor"/> : null;
                                        })()
                                    }
                                >
                                    <UserAvatar size={32} user={initiatorUser}/>
                                </NotificationAvatarBadge>
                            )
                            : null
                    }
                </div>

                <div className="Notification__message__block">
                    <div className={"Notification__message__headerBlock"}>
                        <div className="Notification__message__header body-small">{relatedWs?.name}</div>
                        {isMobile &&
                            <div className="body-small Notification__Actions">
                                <span className="Notification__Date">
                                    {formatDateShort(new Date(notification.createdAt))}
                                </span>
                            </div>
                        }
                    </div>
                    <div className="Notification__message body-small">
                        {getNotificationMsg(rootStore, userState!, notification)}
                    </div>
                </div>
                {
                    !isMobile && (
                        <div className="Notification__rightPart">
                            <div className="body-small Notification__Actions">
                                <Tooltip title={intl.get('clear.common').d("Clear")}>
                                    <DeleteIcon size={16} className="Notification__DeleteIcon"
                                                onClick={() => hideNotification(dataClientService, authService, notification)}/>
                                </Tooltip>
                                <span className="Notification__Date">
                                    {formatDateShort(new Date(notification.createdAt))}
                                </span>
                            </div>
                            <NotificationCardBadge
                                isRead={isRead}
                                onClick={onToggleRead}
                            />
                        </div>
                    )
                }
            </div>
        </div>
    )
}

const getNotificationIcon = (type: string): React.ElementType | null => {
    switch (type) {
        case 'ack_require':
        case 'ack_cancel':
        case 'archivedArticle':
        case 'deleteArticle':
            return ArticleIcon;
        case 'addInWorkspace':
        case 'removeWorkspace':
            return WorkspaceIcon;
        case 'articleMention':
            return MentionIcon;
        case 'takeQuiz':
        case 'retakeQuiz':
            return QuizIcon;
        case 'updatePermission':
        case 'newUser':
            return PersonAdd;
        default:
            return null;
    }
};
